import React from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaDownload, FaMapMarkerAlt } from 'react-icons/fa';
import resumePDF from '../assets/Shalender-resume-2026.pdf';

const Hero = () => {
  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("https://images.unsplash.com/photo-1607971422532-73f9d45d7a47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxkZXZlbG9wZXIlMjBjb2Rpbmd8ZW58MHx8fHwxNzU0MTM4MzEwfDA&ixlib=rb-4.1.0&q=85")',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-24">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Profile Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex justify-center mb-8"
          >
            <div
              className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white/20 shadow-md"
              style={{
                backgroundImage: "url('/Shalender.jpg')",
                backgroundPosition: 'center center',
                backgroundSize: 'contain',
              }}
            ></div>
          </motion.div>

          {/* Name and Title */}
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4"
            >
              Shalender Kumar
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-xl md:text-2xl lg:text-3xl text-blue-200 mb-6 font-light"
            >
              Senior PHP/Laravel Developer
            </motion.h2>
          </div>

          {/* Location and Experience */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8 text-white/90"
          >
            <div className="flex items-center space-x-2">
              <FaMapMarkerAlt className="text-blue-300" />
              <span className="text-lg">Ludhiana, Punjab, India</span>
            </div>
            <div className="text-lg">
              <span className="text-blue-300 font-semibold">11 Years</span> of Experience
            </div>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="max-w-4xl mx-auto space-y-4"
          >
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              Senior Laravel Engineer specializing in scalable SaaS platforms, API-driven systems, and high-performance backend architectures.
            </p>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              11+ years building production systems for SaaS, e-commerce, and enterprise platforms used by global businesses.
            </p>
          </motion.div>

          {/* Credibility Badges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="flex flex-wrap items-center justify-center gap-3 max-w-4xl mx-auto"
          >
            {[
              'SaaS Architecture',
              'API Development',
              'Multi-tenant Systems',
              'High Performance Laravel',
            ].map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm md:text-base font-medium text-white backdrop-blur-sm"
              >
                <FaCheckCircle className="text-emerald-300" />
                {badge}
              </span>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button
              onClick={() => document.getElementById('projects').scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 shadow-md hover:-translate-y-2"
            >
              View My Work
            </button>
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white/30 hover:border-white hover:bg-white/10 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 backdrop-blur-sm"
            >
              Get In Touch
            </button>
            <a
              href={resumePDF}
              download
              className="inline-flex items-center gap-2 border-2 border-blue-300/80 hover:border-blue-200 hover:bg-blue-500/20 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 backdrop-blur-sm"
            >
              <FaDownload />
              Download Resume
            </a>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12"
          >
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-blue-300">11</div>
              <div className="text-white/70 text-sm md:text-base">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-blue-300">10+</div>
              <div className="text-white/70 text-sm md:text-base">Major Projects</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-blue-300">4</div>
              <div className="text-white/70 text-sm md:text-base">Companies</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-blue-300">Laravel</div>
              <div className="text-white/70 text-sm md:text-base">Specialist</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
